import { computed, onScopeDispose, reactive, toRef } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import api from "@/services/api";
import { useAuthStore } from "@/store/auth";
import {
  useActiveOrganizationId,
  useOrganizationScopeReady,
} from "@/services/user";

export const SETUP_PROGRESS_URL = "/setup-progress/";
export const SETUP_PROGRESS_TOURS_URL = "/setup-progress/tours/";
export const SETUP_PROGRESS_DISMISS_URL = "/setup-progress/dismiss/";

export type SetupItemTier = "core" | "advanced" | "tip";
export type SetupItemKind = "organization" | "personal";
export type SetupItemState = "done" | "available" | "locked" | "ask_manager";
export type SetupLockedReason = "needs_resources" | "waiting_for_data";
export type SetupLevel = "explorer" | "wayfinder" | "navigator" | "lightkeeper";
export const SETUP_LEVELS: readonly SetupLevel[] = [
  "explorer",
  "wayfinder",
  "navigator",
  "lightkeeper",
];

export interface SetupItem {
  key: string;
  tier: SetupItemTier;
  kind: SetupItemKind;
  state: SetupItemState;
  lockedReason: SetupLockedReason | null;
  completedBy: { id: number; name: string } | null;
  isNew: boolean;
  dismissed: boolean;
}

export interface SetupCounts {
  done: number;
  open: number;
}

export interface SetupLevelProgress {
  current: SetupLevel;
  wayfinderReachedAt: string | null;
  navigatorReachedAt: string | null;
  lightkeeperLapsed: boolean;
  activeWeeks: number;
  activeWeeksNeeded: number;
  activeWeeksWindow: number;
  activeDaysPerWeek: number;
  advancedDone: number;
  advancedNeeded: number;
  justReached: SetupLevel | null;
  // `earnedIn` is null when that organization is gone or the user lost access.
  earnedElsewhere: boolean;
  earnedLevel: SetupLevel | null;
  earnedIn: string | null;
}

export interface SetupProgress {
  organizationId: number;
  items: SetupItem[];
  summary: { organization: SetupCounts; personal: SetupCounts };
  level: SetupLevelProgress;
}

export type TourStatus = "in_progress" | "completed" | "closed";

export interface TourProgress {
  tourKey: string;
  step: number;
  status: TourStatus;
  updatedAt: string;
}

// Keyed by organization so a switch drops the old entry instead of showing it
// until the new one lands.
export const setupProgressKey = ["setupProgress"] as const;
export function setupProgressQueryKey(organizationId: number | undefined) {
  return [...setupProgressKey, { organizationId }] as const;
}
// This module's own writes never complete an item, so the re-check skips them.
export const SETUP_PROGRESS_MUTATION_KEY = ["setupProgress"] as const;
export const tourProgressKey = ["setupProgressTours"] as const;

function getSetupProgress(): Promise<SetupProgress> {
  return api.get(SETUP_PROGRESS_URL).then((response) => response.data);
}

export function useSetupProgressQuery() {
  const authStore = useAuthStore();
  const organizationId = useActiveOrganizationId();
  const scopeReady = useOrganizationScopeReady();
  return reactive(
    useQuery({
      queryKey: computed(() => setupProgressQueryKey(organizationId.value)),
      queryFn: getSetupProgress,
      enabled: computed(() => authStore.isAuthed && scopeReady.value),
      // Items tick from data saved elsewhere, possibly in another tab.
      refetchOnWindowFocus: true,
      staleTime: 30 * 1000,
    }),
  );
}

// Long enough for a burst of saves (a form that writes twice) to count once,
// short enough that a finished item ticks while the user is still looking.
const RECHECK_DELAY_MS = 600;
// Each re-check runs the evidence queries, so at most one per interval.
const RECHECK_MIN_INTERVAL_MS = 5000;

// A dismissal follows the user, but in an organization with fewer advanced
// items done than Navigator needs, they all still count, so they show.
export function isPutAway(item: SetupItem, level: SetupLevelProgress): boolean {
  return (
    item.dismissed &&
    (item.tier === "tip" || level.advancedDone >= level.advancedNeeded)
  );
}

/** Mount once. */
export function useRecheckSetupProgressAfterSaves() {
  const queryClient = useQueryClient();
  const organizationId = useActiveOrganizationId();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastRecheck = -Infinity;
  const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success") return;
    if (
      event.mutation.options.mutationKey?.[0] === SETUP_PROGRESS_MUTATION_KEY[0]
    ) {
      return;
    }
    // Only an available item can be ticked by the user's own save, so with
    // none open, saves cost no extra request.
    const current = queryClient.getQueryData<SetupProgress>(
      setupProgressQueryKey(organizationId.value),
    );
    if (
      current &&
      !current.items.some(
        (i) => i.state === "available" && !isPutAway(i, current.level),
      )
    ) {
      return;
    }
    clearTimeout(timer);
    const wait = Math.max(
      RECHECK_DELAY_MS,
      lastRecheck + RECHECK_MIN_INTERVAL_MS - Date.now(),
    );
    timer = setTimeout(() => {
      lastRecheck = Date.now();
      void queryClient.invalidateQueries({ queryKey: setupProgressKey });
    }, wait);
  });
  onScopeDispose(() => {
    clearTimeout(timer);
    unsubscribe();
  });
}

export function useInvalidateSetupProgress() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: setupProgressKey });
}

export function useDismissSetupItemMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationKey: SETUP_PROGRESS_MUTATION_KEY,
      mutationFn: (itemKey: string) =>
        api.post(SETUP_PROGRESS_DISMISS_URL, { itemKey }),
      // A refusal means the row was out of date, so refresh either way.
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: setupProgressKey }),
    }),
  );
}

function getTourProgress(): Promise<TourProgress[]> {
  return api.get(SETUP_PROGRESS_TOURS_URL).then((response) => response.data);
}

export function useTourProgressQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: tourProgressKey,
      queryFn: getTourProgress,
      enabled: toRef(authStore, "isAuthed"),
      staleTime: Infinity,
    }),
  );
}

export function useCelebrateLevelMutation() {
  return reactive(
    useMutation({
      mutationKey: SETUP_PROGRESS_MUTATION_KEY,
      mutationFn: (level: SetupLevel) =>
        api.post("/setup-progress/celebrated/", { level }),
    }),
  );
}

export function useSaveTourProgressMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationKey: SETUP_PROGRESS_MUTATION_KEY,
      // Serialized app-wide: a slow "in progress" landing after "closed"
      // would otherwise leave the tour open on the server and in the cache.
      scope: { id: "setupProgressTours" },
      mutationFn: ({
        tourKey,
        step,
        status,
      }: Pick<
        TourProgress,
        "tourKey" | "step" | "status"
      >): Promise<TourProgress> =>
        api
          .put(`${SETUP_PROGRESS_TOURS_URL}${tourKey}/`, { step, status })
          .then((response) => response.data),
      onSuccess: (saved) => {
        queryClient.setQueryData<TourProgress[]>(tourProgressKey, (rows) => [
          ...(rows ?? []).filter((row) => row.tourKey !== saved.tourKey),
          saved,
        ]);
      },
    }),
  );
}
